/**
 * useClientTranscript: opens a client's dialog transcript from the clients
 * list without entering the client card. The transcript dialog is keyed by an
 * estimate id, so on demand the client's estimates are loaded and the most
 * recent one with messages is picked.
 *
 * KAN-710: the dialog opens through ?dialog=<clientId>&estimate=<id>
 * (useDialogParam); TranscriptDialogHost in DashboardLayout renders it.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDialogParam } from '@/hooks/useDialogParam';
import { clientsApi } from '@/services/api/clients';
import type { Client, ClientEstimate } from '@/types/client';

export interface ClientTranscriptState {
  /** Load the client's estimates and open the latest dialog. */
  openFor: (client: Client) => Promise<void>;
  /** Id of the client whose estimates are being loaded, if any. */
  loadingClientId: string | null;
}

function recency(estimate: ClientEstimate): number {
  return new Date(estimate.updated_at ?? estimate.created_at).getTime() || 0;
}

/** Latest estimate with messages; falls back to the latest one overall. */
export function pickTranscriptEstimate(
  estimates: ClientEstimate[]
): ClientEstimate | null {
  if (estimates.length === 0) return null;
  const sorted = [...estimates].sort((a, b) => recency(b) - recency(a));
  return sorted.find((e) => (e.messages_count ?? 0) > 0) ?? sorted[0];
}

export function clientTitle(client: Client): string {
  return client.name || client.phone || 'Без имени';
}

export function useClientTranscript(): ClientTranscriptState {
  const queryClient = useQueryClient();
  const { openDialog } = useDialogParam();
  const [loadingClientId, setLoadingClientId] = useState<string | null>(null);

  const openFor = useCallback(
    async (client: Client) => {
      setLoadingClientId(client.id);
      try {
        const estimates = await queryClient.fetchQuery({
          queryKey: ['client-estimates', client.id],
          queryFn: () => clientsApi.getEstimates(client.id),
        });
        const estimate = pickTranscriptEstimate(estimates);
        if (!estimate) {
          toast.error('У клиента нет переписки');
          return;
        }
        openDialog({
          clientId: client.id,
          estimateId: estimate.id,
          hint: {
            title: clientTitle(client),
            manualMode: client.manual_mode,
            // KAN-636: our Telegram account of the dialog (shown in the header).
            senderAccount: client.sender_account ?? null,
          },
        });
      } catch {
        toast.error('Не удалось открыть переписку');
      } finally {
        setLoadingClientId(null);
      }
    },
    [queryClient, openDialog]
  );

  return { openFor, loadingClientId };
}
